import {Injectable} from '@angular/core';
import {APIService} from "./api.service";
import {User, UserRef, UserStatus, UserToClient} from "../interfaces/user";
import {Paged} from "../interfaces/paged";
import {APIResponse} from "../interfaces/api-response";
import {environment as env} from "../../environments/environment";
import { Holiday } from "../controls/leave-manager/leave-manager.component";
import {ChartData} from "../interfaces/chartData";
import {Delete} from "../interfaces/colDef";
import {ProfileSuggestion} from "../interfaces/profile-suggestion";
import {Experience} from "../controls/experience-assigner/experience";

@Injectable({
  providedIn: 'root'
})
export class UsersService extends APIService{

  public async getAllUsers(){
    return await this.get<User[]>('/users', 'Could not get all Users');
  }

  public async getUsers(status: UserStatus = UserStatus.ACTIVE, page:number = 1, sorts:{col: string, dir: number}[] = []): Promise<Paged<User>>{
    return await this.get<Paged<User>>('/users', 'Could not get Users', {
      params: { status, page, sorts }
    });
  }

  public async findUsers(page: number = 1, filters = null, sorts:{col: string, dir: number}[] = []): Promise<Paged<User>>{
    return await this.post<any, Paged<User>>('/users/find', {
      page,
      sorts,
      filters
    });
  }

  /** Every member with one of these roles (manager-scoped like findUsers), id and name only, unpaged. */
  public async lookupUsers(role: number[]): Promise<UserRef[]> {
    return (await this.get<UserRef[]>('/users/lookup', null, { params: { 'role[]': role } })) ?? [];
  }

  public async findSchedules(page: number = 1, filters = null, sort:{col: string, dir: number} = null): Promise<Paged<any>>{
    return await this.post<any, Paged<any>>('/users/schedule', {
      page,
      sort,
      filters
    });
  }

  async findTimeline(buckets, page: number = 1, size: number = 40, filters = null, sort:{col: string, dir: number} = null): Promise<Paged<any>>{
    return await this.post<any, Paged<any>>('/users/timeline', {
      size,
      page,
      sort,
      buckets,
      filters
    });
  }

  public async getUser(id: number){
    return await this.get<User>('/users/' + id, 'Could not find user: ' + id);
  }

  public async checkUser(email: string){
    return await this.post<{email: string}, User>('/users/check', {
      email
    });
  }

  public async getSchedule(id: number, by:string = 'date'){
    return await this.get<ChartData[]>('/users/' + id + '/schedule', 'Could not get user schedule: ' + id, {
      params: {by}
    });
  }

  public async getLeave(id: number){
    return await this.get<Holiday[]>('/users/' + id + '/leave', 'Could not get leave for user: ' + id);
  }

  public async addUser(user: User): Promise<User> {
    return await this.post<User, User>('/users/', user, user.name_first + ' added!');
  }

  public async changeUserStatus(id: number, status: UserStatus = UserStatus.ACTIVE) {
    const updates: Partial<UserToClient> = { status };
    return await this.saveUser(id, updates, "User " + UserStatus[status]);
  }

  public async deleteUser(id: number) {
    return await this.delete<Delete>('/users/'+id, 'User deleted!');
  }

  public async deleteUserClient(id: number){
    return await this.delete<Delete>('/users/client/' + id, 'User deleted!');
  }

  public async saveUser(id: number, updates:Partial<User|UserToClient>, savemessage = null) {
    return await this.put<Partial<User|UserToClient>, User>('/users/' + id, updates, savemessage);
  }

  public async verifyUser(id: number){
    return await this.post<number[], APIResponse<any>[]>('/users/verify', [id], 'Verification email sent!');
  }

  public async inviteNewUser(user: User) {
    return await this.post<User, boolean>('/createInvite', user, (user?.name_first || '') + ' invited!');
  }

  public async reinviteUser(id: number){
    return await this.post<{id: number}, boolean>('/sendInvite', {id}, 'Invite sent!');
  }

  public async uninviteUser(id: number){
    return await this.post<{id: number}, boolean>('/revokeInvite', {id}, 'Invite revoked!');
  }

  public async approveUser(id: number){
    return await this.post<null, User>('/users/' + id + '/approve', null).catch(() => {
      return null;
    });
  }

  public async export(ids: number[]): Promise<Blob>{
    const myoptions = {responseType: 'blob'};
    const data:Blob = await this.successify(this.errorify(this.http.post<any>(env.api + '/users/export', ids, <any>{...this.apiOptions, ...myoptions}).toPromise()), 'Export Complete');
    return new Blob([data], {type: 'text/csv'});
  }

  public async rejectUser(id: number){
    return await this.post<null, User>('/users/' + id + '/reject', null).catch(() => {
      return null;
    });
  }

  async segments(id: number, from: string|Date, to: string|Date) {
    return await this.post<any, any>('/users/'+id+'/timeline', {
      from,
      to
    });
  }

  async invitedUsers(page: number = 1, filters = null, sorts: { col: string; dir: number; meta?: any }[]) {
    return await this.post<any, Paged<User>>('/users/invited', {
      page,
      sorts,
      filters
    });
  }

  async appliedUsers(page: number = 1, filters = null, sorts: { col: string; dir: number; meta?: any }[]) {
    return await this.post<any, Paged<User>>('/users/applied', {
      page,
      sorts,
      filters
    });
  }

  /**
   * Get AI-powered profile suggestions based on user's CV
   * @param id User ID
   * @returns Suggested skills, certifications, developments, career, and education
   */
  public async suggestProfile(id: number): Promise<ProfileSuggestion> {
    const response = await this.post<null, any>('/users/' + id + '/suggest-profile', null);
    // Unwrap data from API response envelope {status, message, data}
    return response?.data || response;
  }
}
